import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { displayError } from '../../helpers';
import { getAllDeliveryChallans } from '../../services/DeliveryChallanServices';
import { buildListExcelExportHeader } from '../../utils/listExcelExportHeader';
import { exportFormattedExcelReport } from '../../utils/formattedExcelExport';
import { createPdfBlob } from '../../utils/PDFExporter';
import { openPdfInApp } from '../../utils/openPdfInApp';
import {
  TODAY_QTY_SUMMARY_COLUMNS,
  mapDeliveryChallanForTodayQty,
  buildTodayQtySummaryRows,
  formatTodayQtySummaryDisplayRows,
  buildTodayQtySummaryTotals,
  formatQty,
} from './todayQtySummaryUtils';

function TodayQtySummaryModal({
  open,
  onClose,
  cookies,
  plantId,
}) {
  const [loading, setLoading] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [rows, setRows] = useState([]);
  const [companyHeader, setCompanyHeader] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => moment());

  const dateLabel = useMemo(
    () => selectedDate.format('DD-MM-YYYY'),
    [selectedDate],
  );

  const plantIds = useMemo(
    () => (plantId ? [plantId] : []),
    [plantId],
  );

  const loadCompanyHeader = useCallback(async () => {
    try {
      const exportHeader = await buildListExcelExportHeader(cookies, plantIds, {
        documentTitle: "Today's Qty Summary",
      });
      setCompanyHeader(exportHeader);
    } catch (error) {
      setCompanyHeader(null);
    }
  }, [cookies, plantIds]);

  const loadRowsForDate = useCallback(async (dateMoment) => {
    setLoading(true);
    const label = dateMoment.format('DD-MM-YYYY');
    const params = {
      from_date: label,
      upto_date: label,
    };
    if (plantId) {
      params.plant_ids = String(plantId);
    }
    try {
      const response = await getAllDeliveryChallans(cookies, params);
      const mapped = (response?.delivery_challan_list || []).map(mapDeliveryChallanForTodayQty);
      setRows(mapped);
    } catch (error) {
      displayError(error?.response?.data ?? error?.message, 'Failed to load delivery challans');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [cookies, plantId]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const today = moment();
    setSelectedDate(today);
    loadCompanyHeader();
    loadRowsForDate(today);
  }, [open, loadRowsForDate, loadCompanyHeader]);

  const summaryRows = useMemo(() => buildTodayQtySummaryRows(rows), [rows]);
  const displayRows = useMemo(
    () => formatTodayQtySummaryDisplayRows(summaryRows),
    [summaryRows],
  );
  const totals = useMemo(
    () => buildTodayQtySummaryTotals(summaryRows),
    [summaryRows],
  );
  const canExport = !loading && displayRows.length > 0;
  const reportTitle = `Today's Qty Summary (${dateLabel})`;

  const shiftDate = (days) => {
    const nextDate = selectedDate.clone().add(days, 'days');
    setSelectedDate(nextDate);
    loadRowsForDate(nextDate);
  };

  const goToday = () => {
    const today = moment();
    setSelectedDate(today);
    loadRowsForDate(today);
  };

  const handleExcelExport = async () => {
    setExportingExcel(true);
    try {
      const exportHeader = companyHeader || await buildListExcelExportHeader(cookies, plantIds, {
        documentTitle: reportTitle,
      });
      await exportFormattedExcelReport({
        filename: `today_qty_summary_${dateLabel}`,
        documentTitle: "Today's Qty Summary",
        infoLines: [
          ...exportHeader.infoLines,
          `Date: ${dateLabel}`,
          `Total Records: ${summaryRows.length}`,
        ],
        columns: TODAY_QTY_SUMMARY_COLUMNS.map((column) => ({
          key: column.key,
          label: column.label,
          align: column.align || 'left',
        })),
        rows: summaryRows,
        totalKeys: ['dcQty', 'acceptedQty', 'loadCount'],
        header: {
          companyName: exportHeader.companyName,
          addressLines: exportHeader.addressLines,
          gstNo: exportHeader.gstNo,
          logoDataUrl: exportHeader.logoDataUrl,
        },
      });
    } catch (error) {
      displayError(error?.response?.data ?? error, 'Excel export failed');
    } finally {
      setExportingExcel(false);
    }
  };

  const handlePdfExport = async () => {
    setExportingPdf(true);
    try {
      const exportHeader = companyHeader || await buildListExcelExportHeader(cookies, plantIds, {
        documentTitle: "Today's Qty Summary",
        extraInfoLines: [`Date: ${dateLabel}`],
      });
      const pdfColumns = TODAY_QTY_SUMMARY_COLUMNS.map((column) => ({
        header: column.label,
        dataKey: column.key,
        align: column.align === 'right' ? 'right' : 'left',
      }));
      const pdfRows = [
        ...displayRows,
        {
          customer: 'Total',
          site: '',
          grade: '',
          dcQty: formatQty(totals.dcQty),
          acceptedQty: formatQty(totals.acceptedQty),
          loadCount: totals.loadCount,
          marketing: '',
        },
      ];
      const blob = createPdfBlob(
        '',
        pdfColumns,
        pdfRows,
        exportHeader.infoLines?.length
          ? exportHeader.infoLines
          : [`Date: ${dateLabel}`],
        {
          companyName: exportHeader.companyName,
          addressLines: exportHeader.addressLines,
          gstNo: exportHeader.gstNo,
          logoDataUrl: exportHeader.logoDataUrl,
          documentTitle: "Today's Qty Summary",
          subtitle: dateLabel,
        },
      );
      openPdfInApp(blob, {
        title: reportTitle,
        downloadFileName: `today_qty_summary_${dateLabel}.pdf`,
      });
    } catch (error) {
      displayError(error?.response?.data ?? error, 'PDF export failed');
    } finally {
      setExportingPdf(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div className="modal today-qty-summary-modal" tabIndex="-1" style={{ display: 'block' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header today-qty-summary-modal-header">
            <h5 className="modal-title">{reportTitle}</h5>
            <div className="today-qty-summary-header-actions">
              {canExport && (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    title="View PDF"
                    aria-label="View PDF"
                    disabled={exportingPdf}
                    onClick={handlePdfExport}
                  >
                    <i className="fa fa-file-pdf-o text-danger" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    title="Download Excel"
                    aria-label="Download Excel"
                    disabled={exportingExcel}
                    onClick={handleExcelExport}
                  >
                    <i className="fa fa-file-excel-o text-success" aria-hidden="true" />
                  </button>
                </>
              )}
              <button type="button" className="close text-white" aria-label="Close" onClick={onClose}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </div>
          <div className="modal-body">
            {loading ? (
              <div className="text-center py-4">Loading...</div>
            ) : displayRows.length === 0 ? (
              <div className="text-center py-4 text-muted">
                No delivery challans found for {dateLabel}.
              </div>
            ) : (
              <div className="table-responsive no-column-freeze">
                <table className="table table-bordered today-qty-summary-table">
                  <thead>
                    <tr>
                      {TODAY_QTY_SUMMARY_COLUMNS.map((column) => (
                        <th
                          key={column.key}
                          className={column.align === 'right' ? 'text-right' : undefined}
                        >
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.map((row, index) => (
                      <tr key={`${row.customer}-${row.site}-${row.grade}-${row.marketing}-${index}`}>
                        {TODAY_QTY_SUMMARY_COLUMNS.map((column) => (
                          <td
                            key={column.key}
                            className={column.align === 'right' ? 'text-right' : undefined}
                          >
                            {row[column.key]}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="today-qty-summary-total-row">
                      <td>Total</td>
                      <td />
                      <td />
                      <td className="text-right">{formatQty(totals.dcQty)}</td>
                      <td className="text-right">{formatQty(totals.acceptedQty)}</td>
                      <td className="text-right">{totals.loadCount}</td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="modal-footer today-qty-summary-modal-footer">
            <div className="today-qty-summary-nav">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => shiftDate(-1)}
                disabled={loading}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={goToday}
                disabled={loading}
              >
                Today
              </button>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => shiftDate(1)}
                disabled={loading}
              >
                Next
              </button>
            </div>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TodayQtySummaryModal;
